// Maps each `*-for-sale` URL slug to its typesense ride type and a display
// label used in page metadata. The single `[vehiclesForSale]` dynamic route
// serves every vehicle type listed here. The `'All'` ride type is a synthetic
// value used by `/vehicles-for-sale` to aggregate every vehicle category.
export const VEHICLE_FOR_SALE_CONFIG = {
  'dirt-bikes-for-sale': { rideType: 'Dirt Bikes', label: 'Dirt Bikes' },
  'atvs-for-sale': { rideType: 'ATV', label: 'ATVs' },
  'utvs-for-sale': { rideType: 'UTV', label: 'UTVs' },
  'street-bikes-for-sale': { rideType: 'Street', label: 'Street Bikes' },
  'snowmobiles-for-sale': { rideType: 'Snow', label: 'Snowmobiles' },
  'pwcs-for-sale': { rideType: 'PWC', label: 'PWCs' },
  'e-moto-bikes-for-sale': { rideType: 'E-Moto', label: 'E-Moto Bikes' },
  'vehicles-for-sale': { rideType: 'All', label: 'Vehicles' },
} as const;

export type VehicleForSaleSlug = keyof typeof VEHICLE_FOR_SALE_CONFIG;

// Finds which vehicle-for-sale slug a path contains, e.g.
// `honda-atvs-for-sale` -> `atvs-for-sale`. Returns undefined when none match.
export const matchVehicleForSaleSlug = (pathName: string) =>
  (Object.keys(VEHICLE_FOR_SALE_CONFIG) as VehicleForSaleSlug[]).find((slug) =>
    pathName.includes(slug)
  );

// Derived from `VEHICLE_FOR_SALE_CONFIG` so the two maps cannot drift. Skips
// the synthetic `'All'` ride type — it has no canonical category to map back.
const CATEGORY_TO_SLUG: Record<string, VehicleForSaleSlug> = Object.fromEntries(
  (
    Object.entries(VEHICLE_FOR_SALE_CONFIG) as [
      VehicleForSaleSlug,
      (typeof VEHICLE_FOR_SALE_CONFIG)[VehicleForSaleSlug]
    ][]
  )
    .filter(([, { rideType }]) => rideType !== 'All')
    .map(([slug, { rideType }]) => [rideType, slug])
);

export const getVehicleForSaleSlugForCategory = (
  category: string | undefined | null
): VehicleForSaleSlug =>
  CATEGORY_TO_SLUG[category ?? ''] ?? 'vehicles-for-sale';
